import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Global Master / Type — parent data types (GlobalMaster) and their child values
 * (GlobalTypeMaster), related by value (GlobalType.dataType === GlobalMaster.dataTypeName).
 *
 * The list endpoints return Dapper dynamic rows, which serialize PascalCase, so reads are
 * normalized defensively (PascalCase → camelCase) the same way services/payment.ts does.
 * Writes POST a camelCase DTO (the API binds case-insensitively).
 */

// ===================== models =====================
export interface GlobalMaster {
  id: number;
  dataTypeName: string;
  displayName?: string | null;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdDate: string;
  modifiedDate?: string | null;
  valueCount: number;
}
export interface GlobalMasterDto {
  id: number; // 0 => insert, >0 => update
  dataTypeName: string;
  displayName?: string | null;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
}
export interface GlobalType {
  id: number;
  dataType: string; // = parent dataTypeName
  dataValue: string;
  displayOrder?: number | null;
  isActive: boolean;
  createdBy?: string | null;
  createdDate: string;
  typeDisplayName?: string | null;
}
export interface GlobalTypeDto {
  id: number; // 0 => insert, >0 => update
  dataType: string;
  dataValue: string;
  displayOrder?: number | null;
  isActive: boolean;
}
/** Normalized active-parent option for the value dialog's data-type `p-select`. */
export interface ActiveType {
  value: string; // = dataTypeName (the key children reference)
  label: string; // displayName || dataTypeName
}

// ===================== PascalCase → camelCase normalization =====================
function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStr(v: any): string | null {
  return v == null ? null : String(v);
}

function mapGm(x: any): GlobalMaster {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    dataTypeName: pick(x, 'DataTypeName', 'dataTypeName') ?? '',
    displayName: asStr(pick(x, 'DisplayName', 'displayName')),
    description: asStr(pick(x, 'Description', 'description')),
    sortOrder: asNum(pick(x, 'SortOrder', 'sortOrder')),
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? '',
    modifiedDate: asStr(pick(x, 'ModifiedDate', 'modifiedDate')),
    valueCount: asNum(pick(x, 'ValueCount', 'valueCount')),
  };
}
function mapGt(x: any): GlobalType {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    dataType: pick(x, 'DataType', 'dataType') ?? '',
    dataValue: pick(x, 'DataValue', 'dataValue') ?? '',
    displayOrder: pick<number>(x, 'DisplayOrder', 'displayOrder') ?? null,
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdBy: asStr(pick(x, 'CreatedBy', 'createdBy')),
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? '',
    typeDisplayName: asStr(pick(x, 'TypeDisplayName', 'typeDisplayName')),
  };
}
function mapActive(x: any): ActiveType {
  const name = pick<string>(x, 'DataTypeName', 'dataTypeName') ?? '';
  const display = pick<string>(x, 'DisplayName', 'displayName');
  return { value: name, label: display || name };
}

/** Apply a row-mapper to an envelope whose `data` is an array. */
function mapList<T>(fn: (x: any) => T) {
  return (res: any): ApiEnvelope<T[]> =>
    res && Array.isArray(res.data) ? { ...res, data: res.data.map(fn) } : { ...res, data: [] };
}
/** Apply a row-mapper to an envelope whose `data` is a single object. */
function mapObj<T>(fn: (x: any) => T) {
  return (res: any): ApiEnvelope<T> =>
    res?.data && typeof res.data === 'object' ? { ...res, data: fn(res.data) } : res;
}

@Injectable({ providedIn: 'root' })
export class GlobalMasterService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  // ----- GlobalMaster (parent) -----
  gmAll() {
    return this.http
      .get<ApiEnvelope<any[]>>(`${this.api}/GlobalMaster/GetAll`)
      .pipe(map(mapList(mapGm)));
  }
  gmActive() {
    return this.http
      .get<ApiEnvelope<any[]>>(`${this.api}/GlobalMaster/GetActive`)
      .pipe(map(mapList(mapActive)));
  }
  gmGet(id: number) {
    return this.http
      .get<ApiEnvelope<any>>(`${this.api}/GlobalMaster/GetById`, { params: { id } })
      .pipe(map(mapObj(mapGm)));
  }
  gmInsert(m: GlobalMasterDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/GlobalMaster/Insert`, m);
  }
  gmUpdate(m: GlobalMasterDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/GlobalMaster/Update`, m);
  }
  gmEnable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalMaster/Enable`, { params: { id } });
  }
  gmDisable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalMaster/Disable`, { params: { id } });
  }
  gmDelete(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalMaster/Delete`, { params: { id } });
  }

  // ----- GlobalType (child) -----
  gtByType(dataType: string) {
    return this.http
      .get<ApiEnvelope<any[]>>(`${this.api}/GlobalType/GetByType`, { params: { dataType } })
      .pipe(map(mapList(mapGt)));
  }
  gtInsert(m: GlobalTypeDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/GlobalType/Insert`, m);
  }
  gtUpdate(m: GlobalTypeDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/GlobalType/Update`, m);
  }
  gtEnable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalType/Enable`, { params: { id } });
  }
  gtDisable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalType/Disable`, { params: { id } });
  }
  gtDelete(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/GlobalType/Delete`, { params: { id } });
  }
}
